import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  IconButton,
  Grid,
  Divider,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import { useDispatch } from 'react-redux';
import { colorPalette } from '@utils/colorPalette';
import emptyProductImage from '@assets/icons/empty-product.jpg';
import { GetProductCatalogs, GetSubCategories, GetCategories, GetProducts, GetAllProductsCount, GetProductsLoading, GetCartDetails, GetUser } from "@redux-state/selectors";
import ProductsView from '@pages/products/Products/ProductsView';
import { getProducts, addToCart } from '@redux-state/common/action';
import { Api } from '@redux-state/common/api';
import { addRemoveToWishlist } from '@redux-state/common/action';
import { GetWishlistLoading } from '@redux-state/common/selectors';
import { CustomCarousel } from '../CustomCarousal';

export const ProductModal = ({ filter, isRTL, open, setOpen, product, imageUrls, finalPrice, hasDiscount }) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [wishListItem, setWishListItem] = useState(false);

  const loopRef = useRef();

  const handleOpen = (value) => setOpen(value);

  const dispatch = useDispatch();

  const allCategories = GetCategories();
  const subCategories = GetSubCategories();
  const products = GetProducts();
  const itemsCount = GetAllProductsCount();
  const isFetching = GetProductsLoading();
  const cartDetails = GetCartDetails();
  const wishListLoading = GetWishlistLoading();
  const user = GetUser();

  const handleIncrease = (product, finalPrice) => {
    const existingProductIndex = cartDetails?.items.findIndex(item => item.id === product.id);

    if (existingProductIndex !== -1) {
      const updatedItems = cartDetails.items.map((item, index) => {
        if (index === existingProductIndex) {
          if (item.quantity === parseInt(item?.qty_onhand)) {
            alert(isRTL
              ? `لا يمكنك إضافة أكثر من ${item?.qty_onhand}. لدينا فقط ${item?.qty_onhand} قطعة في المخزون.`
              : `You cannot add more than ${item?.qty_onhand}. We have only ${item?.qty_onhand} items in stock`);
            return item;
          }
          return {
            ...item,
            quantity: item.quantity + 1,
            totalPrice: (item.quantity + 1) * Number(finalPrice)
          };
        }
        return item;
      });

      const newTotalPrice = updatedItems.reduce((sum, item) => sum + Number(item.totalPrice), 0);
      dispatch(addToCart({ items: updatedItems, user: user, totalPrice: newTotalPrice }));
    } else {
      const updatedItems = [
        ...cartDetails.items,
        { ...product, quantity: 1, totalPrice: finalPrice }
      ];

      const newTotalPrice = updatedItems.reduce((sum, item) => sum + Number(item.totalPrice), 0);
      dispatch(addToCart({ items: updatedItems, user: user, totalPrice: newTotalPrice }));
    }
  };

  const handleDecrease = (product, finalPrice) => {
    const existingProductIndex = cartDetails?.items.findIndex(item => item.id === product.id);

    if (existingProductIndex !== -1) {
      const updatedItems = cartDetails.items.map((item, index) => {
        if (index === existingProductIndex) {
          const newQuantity = item.quantity > 1 ? item.quantity - 1 : 0;
          if (newQuantity === 0) {
            return null; // Remove the item
          }
          return {
            ...item,
            quantity: newQuantity,
            totalPrice: newQuantity * Number(finalPrice)
          };
        }
        return item;
      }).filter(item => item !== null);

      const newTotalPrice = updatedItems.reduce((sum, item) => sum + Number(item.totalPrice), 0);
      dispatch(addToCart({ items: updatedItems, user: user, totalPrice: newTotalPrice }));
    }
  };

  const pagination = useMemo(
    () => ({
      page: 0,
      perPage: rowsPerPage,
    }),
    [rowsPerPage]
  );

  useEffect(() => {
    const handleScroll = () => {
      const element = loopRef.current;
      if (!element) return;
      const isBottomReached = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
      if (isBottomReached) {
        setRowsPerPage((prevRows) => prevRows + 10);
      }
    };

    const element = loopRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loopRef?.current?.scrollTop]);

  window.onbeforeunload = function () {
    window.scrollTo(0, 0);
  };

  const fetchFeedData = useCallback(() => {
    if (itemsCount > 0 && itemsCount <= pagination.perPage) {
      setHasMoreItems(false);
    }
  }, [dispatch, pagination, itemsCount, pagination.perPage]);

  useEffect(() => {
    fetchFeedData();
  }, [pagination.perPage, fetchFeedData]);

  const allProductCatalogs = GetProductCatalogs();
  const splitByTypeAndLanguage = (array) => {
    return array.reduce((acc, item) => {
      const { type } = item;
      const language = 'en';
      if (!acc[language]) {
        acc[language] = {};
      }
      if (!acc[language][type]) {
        acc[language][type] = [];
      }
      acc[language][type].push(item);
      return acc;
    }, {});
  };

  const {
    en: {
      brand: enBrands = [],
      'available-color': enAvailableColors = [],
      material: enMaterials = [],
      unit: enUnits = [],
      size: enSizes = []
    } = {},
  } = splitByTypeAndLanguage(allProductCatalogs);

  const category = allCategories.find(category => category?.id?.toString() === product?.webCategory?.toString())
  const subCategory = subCategories.find(subcategory => subcategory?.id?.toString() === product?.subCategory?.toString())

  const enProductSize = enSizes.find(size => size?.id?.toString() === product?.size?.toString())
  const arProductSize = enSizes.find(size => size?.id?.toString() === product?.ar_size?.toString())

  const enProductBrand = enBrands.find(size => size?.id?.toString() === product?.brand?.toString())
  const arProductBrand = enBrands.find(size => size?.id?.toString() === product?.ar_brand?.toString())

  const enProductUnit = enUnits.find(unit => unit?.id?.toString() === product?.unit?.toString())
  const arProductUnit = enUnits.find(unit => unit?.id?.toString() === product?.ar_unit?.toString())

  const enProductMaterial = enMaterials.find(mat => mat?.id?.toString() === product?.material?.toString())
  const arProductMaterial = enMaterials.find(mat => mat?.id?.toString() === product?.ar_material?.toString())

  const enProductColor = enAvailableColors.find(color => color?.id?.toString() === product?.avalable_color?.toString())
  const arProductColor = enAvailableColors.find(color => color?.id?.toString() === product?.ar_color?.toString())

  const size = isRTL ? enSizes.find(size => size?.id?.toString() === arProductSize?.id?.toString()) : enSizes.find(size => size?.id?.toString() === enProductSize?.id?.toString())
  const brand = isRTL ? enBrands.find(brand => brand?.id?.toString() === arProductBrand?.id?.toString()) : enBrands.find(brand => brand?.id?.toString() === enProductBrand?.id?.toString())
  const unit = isRTL ? enUnits.find(unit => unit?.id?.toString() === arProductUnit?.id?.toString()) : enUnits.find(unit => unit?.id?.toString() === enProductUnit?.id?.toString())
  const material = isRTL ? enMaterials.find(material => material?.id?.toString() === arProductMaterial?.id?.toString()) : enMaterials.find(material => material?.id?.toString() === enProductMaterial?.id?.toString())
  const color = isRTL ? enAvailableColors.find(color => color?.id?.toString() === arProductColor?.id?.toString()) : enAvailableColors.find(color => color?.id?.toString() === enProductColor?.id?.toString())

  const existingProduct = cartDetails?.items.find(item => item.id === product.id);

  const qty = parseInt(product?.qty_onhand);

  const handleImageChange = (index) => {
    setSelectedIndex(index);
  };
  const gallery = [emptyProductImage];
  const images = imageUrls?.length > 0 ? imageUrls : gallery;

  const loadProducts = () => {
    const query = isRTL ? { arabicCategory: category?.id } : { webCategory: category?.id };
    dispatch(getProducts(pagination, query));
  }

  const checkWishlistProduct = async () => {
    const isWishlistProduct = await Api.checkWishlistProduct(product?.id, user?.id)
    setWishListItem(isWishlistProduct);
  }

  useEffect(() => {
    checkWishlistProduct();
    // eslint-disable-next-line
  }, [wishListLoading]);

  const wishlistTooltipText = () => {
    if (isRTL) {
      return wishListItem ? "إزالة من قائمة الأمنيات" : "إضافة إلى قائمة الأمنيات";
    }
    return wishListItem ? "Remove from Wishlist" : "Add to Wishlist"
  }

  const addRemoveInWishlist = () => {
    dispatch(addRemoveToWishlist({ productId: product.id, userId: user.id, type: wishListItem ? 'remove' : 'add' }));
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(null)}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          p: { xs: 1, sm: 2, md: 3 },
          width: '100%',
        }
      }}
    >
      <DialogActions sx={{ p: { xs: 1, sm: 2 }, pb: 0 }}>
        <IconButton
          onClick={() => setOpen(null)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 100,
            bgcolor: "#fff",
            boxShadow: 1,
            '&:hover': { bgcolor: colorPalette.lightShadow }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogActions>
      <DialogContent
        ref={loopRef}
        sx={{
          overflowY: 'auto',
          p: { xs: 1, sm: 3 },
        }}
      >
        <Grid container spacing={2}>
          {/* Left Section: Carousel */}
          <Grid item xs={12} md={6} sx={{ mb: { xs: 2, md: 0 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxWidth: { xs: '100%', md: 400 },
                mx: 'auto',
              }}
            >
              <CustomCarousel selectedIndex={selectedIndex} handleImageChange={handleImageChange} images={images} isRTL={isRTL} />
            </Box>
          </Grid>

          {/* Right Section: Details */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
              <Typography variant="h5" sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '80%',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                fontWeight: 'bold',
                fontSize: { xs: 19, sm: 24 },
                marginBottom: 1,
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}>
                {isRTL ? product?.arabicName : product?.website_name}
              </Typography>
              {user && <Tooltip title={wishlistTooltipText()} arrow>
                <IconButton
                  onClick={addRemoveInWishlist}
                  sx={{
                    color: colorPalette.theme,
                    padding: 1,
                    border: `1px solid ${wishListItem ? colorPalette.theme : colorPalette.lightGrey}`,
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                  }}
                >
                  {wishListLoading ? <CircularProgress size={20} /> : wishListItem ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                </IconButton>
              </Tooltip>}
            </Box>
            {/* Product Attributes */}
            {[{
              label: isRTL ? "مقاس: " : "Size: ", value: isRTL ? size?.ar_title : size?.title
            }, {
              label: isRTL ? "ماركة: " : "Brand: ", value: isRTL ? brand?.ar_title : brand?.title
            }, {
              label: isRTL ? "وحدة: " : "Unit: ", value: isRTL ? unit?.ar_title : unit?.title
            }, {
              label: isRTL ? "لون: " : "Color: ", value: isRTL ? color?.ar_title : color?.title
            }, {
              label: isRTL ? "رمز العنصر: " : "Material: ", value: isRTL ? material?.ar_title : material?.title
            }, {
              label: isRTL ? "الباركود: " : "Item Code: ", value: product?.barcode
            }].map((attr, idx) =>
              <Typography
                key={idx}
                variant="body2"
                mt={2}
                color="textSecondary"
                sx={{
                  mb: 1, direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                  fontSize: { xs: 13, sm: 15 }
                }}>
                {attr.label}{attr.value}
              </Typography>
            )}

            {/* Price */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                mt: 2,
                color: colorPalette.theme,
                mb: 1,
                fontSize: { xs: 22, sm: 28 }
              }}
            >
              {isRTL ? "ر۔ع  " : "OMR  "}{finalPrice}
              {hasDiscount && <Typography
                variant="body2"
                component="span"
                color='textDisabled'
                sx={{
                  textDecoration: 'line-through',
                  mx: 2,
                  fontSize: { xs: 13, sm: '1rem' },
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                }}
              >
                {isRTL ? "ر۔ع" : "OMR"} {product?.price}
              </Typography>}
            </Typography>
            {/* Cart/Add controls */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-start',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
              mt: 2
            }}>
              {qty > 0 && <Button
                variant="contained"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  handleIncrease(product, finalPrice);
                }}
                size="large"
                sx={{
                  textTransform: 'capitalize',
                  background: colorPalette.theme,
                  width: { xs: '100%', sm: '60%' },
                  fontSize: { xs: 15, sm: 16 },
                  px: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {existingProduct && <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleIncrease(product, finalPrice);
                  }}
                  sx={{ color: colorPalette.white, p: 0 }}
                  size="small"
                >
                  <AddIcon />
                </IconButton>}

                <Typography
                  sx={{
                    color: colorPalette.white,
                    flexGrow: 1,
                    textAlign: 'center',
                    fontWeight: 600,
                  }}
                >
                  {existingProduct ? existingProduct?.quantity : (isRTL ? "أضف إلى سلة التسوق" : "Add to Shopping Cart")}
                </Typography>

                {existingProduct && <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDecrease(product, finalPrice);
                  }}
                  sx={{ color: colorPalette.white, p: 0 }}
                  size="small"
                >
                  <RemoveIcon />
                </IconButton>}
              </Button>}

              <Typography
                sx={{
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                  mt: { xs: 1, sm: 0 },
                  fontSize: { xs: 13, sm: 14 }
                }}
                variant="body1"
                color="textSecondary"
              >
                {qty > 0 ? `${product?.qty_onhand} ${isRTL ? "القطع المتاحة" : "pieces available"}` : `${isRTL ? "إنتهى من المخزن" : "out of stock"}`}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* Category/Subcategory */}
            {category &&
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                <strong dir={isRTL && "rtl"}>{isRTL ? "فئات: " : "Categories:"}</strong>
                <Button
                  variant="outlined"
                  size='small'
                  sx={{
                    textTransform: 'lowercase',
                    color: colorPalette.black,
                    borderColor: colorPalette.lavenderGray,
                    ml: 2
                  }}
                >
                  {isRTL ? category?.ar_category : category?.category}
                </Button>
                {subCategory && <Button
                  variant="outlined"
                  size='small'
                  sx={{
                    textTransform: 'lowercase',
                    color: colorPalette.black,
                    borderColor: colorPalette.lavenderGray,
                    ml: 2
                  }}
                >
                  {isRTL ? subCategory?.ar_subcategory : subCategory?.subcategory}
                </Button>}
              </Typography>
            }
          </Grid>
        </Grid>
        <Divider sx={{ mb: { xs: 3, sm: 6 } }} />
        {/* Product Details */}
        <Typography
          sx={{
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left',
            fontWeight: 550,
            mb: 1,
            fontSize: { xs: 16, sm: 18 }
          }}
          variant="h6"
        >
          {isRTL ? "تفصیلات" : "Details"}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          dangerouslySetInnerHTML={{
            __html: (isRTL ? product?.arabicDescription : product?.webDescription) + ''
          }}
          sx={{
            mb: 2, direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left',
            fontSize: { xs: 13, sm: 15 }
          }}
        />
        <Divider sx={{ mb: { xs: 3, sm: 6 } }} />
        {/* Related Products */}
        <Typography
          sx={{
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left',
            fontWeight: 550,
            mb: { xs: 2, sm: 5 },
            fontSize: { xs: 16, sm: 18 }
          }}
          variant="h6"
        >
          {isRTL ? "المنتجات ذات الصلة" : "Related Products"}
        </Typography>

        <ProductsView
          hasMoreItems={hasMoreItems}
          loadProducts={loadProducts}
          isFetching={isFetching}
          products={products}
          isRTL={isRTL}
          open={open}
          handleOpen={handleOpen}
          setOpen={setOpen}
        />
      </DialogContent>
    </Dialog>
  );
};
